/**
 * Time rendering for the Scheduler.
 *
 * Every function here takes an explicit timezone. The browser's own zone is
 * never used to render a slot: the therapist's timezone is the only correct
 * frame of reference for both therapist and patient.
 */

// The zone list and its labels are shared with the patient record, which
// carries a timezone of its own; re-exported so the Scheduler's callers keep
// importing them from here.
export {
    detectBrowserTimezone,
    supportedTimezones,
    timezoneLabel,
} from "../../../shared/domain/timezones";

export function formatSlotTime(isoInstant: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone,
    }).format(new Date(isoInstant));
}

export function formatSlotDate(isoInstant: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone,
    }).format(new Date(isoInstant));
}

/** The zone abbreviation, e.g. `CEST` — shown next to every time. */
export function timezoneAbbreviation(
    isoInstant: string,
    timeZone: string
): string {
    const parts = new Intl.DateTimeFormat(undefined, {
        timeZone,
        timeZoneName: "short",
    }).formatToParts(new Date(isoInstant));
    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
}

/** `10:00 CEST` — the pairing the specs require everywhere a time is shown. */
export function formatSlotTimeWithZone(
    isoInstant: string,
    timeZone: string
): string {
    const abbreviation = timezoneAbbreviation(isoInstant, timeZone);
    const time = formatSlotTime(isoInstant, timeZone);
    return abbreviation ? `${time} ${abbreviation}` : time;
}

/** `YYYY-MM-DD` for the given instant in the given zone. */
export function toDateKey(date: Date, timeZone: string): string {
    const parts = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone,
    }).formatToParts(date);
    const lookup = (type: string) =>
        parts.find((part) => part.type === type)?.value ?? "";
    return `${lookup("year")}-${lookup("month")}-${lookup("day")}`;
}

/** Weekday of a `YYYY-MM-DD` key, Monday = 0, matching the backend. */
export function weekdayOfDateKey(dateKey: string): number {
    // Midday keeps the reading away from any DST boundary.
    return (new Date(`${dateKey}T12:00:00`).getDay() + 6) % 7;
}

/** Current wall-clock time in `timeZone`, as `HH:MM:SS` for string comparison. */
export function localTimeOfDay(at: Date, timeZone: string): string {
    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone,
    }).format(at);
}

/** The shape `nextDateWithAvailability` needs — a weekly working window. */
export interface WorkingWindow {
    day_of_week: number;
    /** `HH:MM:SS` in the therapist's timezone. */
    end_time: string;
    is_active: boolean;
}

/**
 * The first date from `from` on which the therapist can still be booked.
 *
 * A weekday having hours is not enough: today only counts if some window has
 * not already ended, otherwise the caller lands on a day whose slots are all
 * in the past and the empty list reads as a fault.
 *
 * Returns `from` unchanged when no window is active, or when none matches
 * within a fortnight — the caller then shows the "no hours configured" state
 * rather than silently landing on an arbitrary day.
 */
export function nextDateWithAvailability(
    from: Date,
    windows: readonly WorkingWindow[],
    timeZone: string
): string {
    const start = toDateKey(from, timeZone);
    const active = windows.filter((window) => window.is_active);
    if (active.length === 0) return start;

    const nowTime = localTimeOfDay(from, timeZone);

    for (let offset = 0; offset < 14; offset += 1) {
        const candidate = new Date(from);
        candidate.setDate(candidate.getDate() + offset);
        const key = toDateKey(candidate, timeZone);
        const weekday = weekdayOfDateKey(key);

        const onThatDay = active.filter((window) => window.day_of_week === weekday);
        if (onThatDay.length === 0) continue;

        // Only today can have windows that have already closed.
        const stillOpen =
            key !== start || onThatDay.some((window) => window.end_time > nowTime);
        if (stillOpen) return key;
    }
    return start;
}

/** `YYYY-MM` for a month view. */
export function toMonthKey(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    return `${date.getFullYear()}-${month}`;
}

/**
 * The last date key of the week `dateKey` falls in, weeks running Monday to
 * Sunday.
 *
 * Sunday is the awkward case: `getDay()` calls it 0, which would otherwise
 * place it at the *start* of the coming week rather than the end of the one
 * just finished.
 */
export function endOfWeekKey(dateKey: string): string {
    const [year, month, day] = dateKey.split("-").map(Number);
    const at = new Date(Date.UTC(year, month - 1, day));
    const weekday = at.getUTCDay() === 0 ? 7 : at.getUTCDay();
    at.setUTCDate(at.getUTCDate() + (7 - weekday));
    return at.toISOString().slice(0, 10);
}

/**
 * The Monday of the week `dateKey` falls in.
 *
 * Sunday is the awkward case again: `getDay()` calls it 0, which would send it
 * back to the Monday of the *coming* week instead of the one it closes.
 */
export function startOfWeekKey(dateKey: string): string {
    const [year, month, day] = dateKey.split("-").map(Number);
    const at = new Date(Date.UTC(year, month - 1, day));
    const weekday = at.getUTCDay() === 0 ? 7 : at.getUTCDay();
    at.setUTCDate(at.getUTCDate() - (weekday - 1));
    return at.toISOString().slice(0, 10);
}

/** `dateKey` moved by `days`, which may be negative. */
export function shiftDateKey(dateKey: string, days: number): string {
    const [year, month, day] = dateKey.split("-").map(Number);
    const at = new Date(Date.UTC(year, month - 1, day));
    at.setUTCDate(at.getUTCDate() + days);
    return at.toISOString().slice(0, 10);
}

/** The seven day keys of the week starting at `weekStart`, Monday first. */
export function weekDayKeys(weekStart: string): string[] {
    return Array.from({ length: 7 }, (_, index) =>
        shiftDateKey(weekStart, index)
    );
}

/** Whether `dateKey` falls in the same Monday-to-Sunday week as `reference`. */
export function isInSameWeek(dateKey: string, reference: string): boolean {
    return dateKey >= reference && dateKey <= endOfWeekKey(reference);
}

/**
 * `2026-09-02 · 09:00 CEST` for a stored booking.
 *
 * A stored session carries a naive local date and hour rather than an instant,
 * so `formatSlotTimeWithZone` cannot read it directly. Reconstructing the
 * instant here means every surface shows one format, and none of them has to
 * fall back to printing the raw IANA identifier at the reader.
 *
 * Returns the bare time when the zone is not yet known, rather than labelling
 * a local time with a guess — briefly claiming 09:00 Madrid is 09:00 UTC is
 * worse than saying nothing.
 */
export function formatBookingTime(
    dateKey: string,
    hour: string,
    timeZone: string | null
): string {
    if (!timeZone) return hour;
    const instant = instantOf(dateKey, hour, timeZone);
    return instant ? formatSlotTimeWithZone(instant, timeZone) : hour;
}

/**
 * The UTC instant a local wall-clock reading corresponds to in `timeZone`.
 *
 * Found by guessing UTC and correcting by the offset that guess implies, which
 * settles in one step for every zone with a whole-minute offset.
 */
export function instantOf(
    dateKey: string,
    hour: string,
    timeZone: string
): string | null {
    const [year, month, day] = dateKey.split("-").map(Number);
    const [hours, minutes] = hour.split(":").map(Number);
    // `Number.isFinite`, not `Number.isNaN`: a short string yields `undefined`
    // for the missing parts, and `Number.isNaN(undefined)` is false.
    if (![year, month, day, hours, minutes].every(Number.isFinite)) return null;

    const guess = Date.UTC(year, month - 1, day, hours, minutes);
    const asSeenInZone = new Date(
        new Date(guess).toLocaleString("en-US", { timeZone })
    ).getTime();
    const asSeenInUtc = new Date(
        new Date(guess).toLocaleString("en-US", { timeZone: "UTC" })
    ).getTime();
    return new Date(guess + (asSeenInUtc - asSeenInZone)).toISOString();
}
